import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PermissionGate from '../../../components/common/PermissionGate';
import ExportModal from '../../../components/common/ExportModal';
import Button from '../../../components/common/Button/Button';
import margDataService from '../../../api/crm/marg/margDataService';
import orderService from '../../../api/crm/orders/orderService';
import { formatDate } from '../../../utils/stringUtils';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import { toast } from 'react-hot-toast';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import customerService from '../../../api/crm/customers/customerService';
import { formatIndianAmount, formatIndianNumber } from '../../../utils/amountFormatter';
import { get } from '../../../api/apiClient';
import { Package, Search, Filter, Eye, Edit, Phone, MapPin, Calendar, User, Plus, ArrowLeft, X, ArrowUpDown, ArrowUp, ArrowDown, Download, Upload, Truck, CheckCircle, Clock, AlertCircle, IndianRupee } from 'lucide-react';
import AddPaymentModal from '../../../components/crm/AddPaymentModal';
import BulkPaymentImportModal from '../../../components/crm/BulkPaymentImportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { useCallback } from 'react';
import {
  handleSearchInputChange,
  sanitizeSearchInput,
  FIELD_LIMITS,
} from '../../../utils/inputValidation';

const OrderManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  const searchInputRef = useRef(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('limit')) || 10);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [analytics, setAnalytics] = useState({ totalInvoices: 0, totalRevenue: 0 });
  const [globalKpis, setGlobalKpis] = useState({ totalInvoices: 0, totalRevenue: 0, totalPaid: 0, totalOutstanding: 0, delivered: 0, inTransit: 0, outForDelivery: 0, rto: 0 });
  const [paymentSummary, setPaymentSummary] = useState({ totalPaid: 0, totalOutstanding: 0 });
  const [isSyncingInBackground, setIsSyncingInBackground] = useState(false);
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'date');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || ''
  });
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [viewType, setViewType] = useState(searchParams.get('viewType') || 'S');

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = [
        'search', 'status', 'viewType', 'startDate', 'endDate',
        'page', 'limit', 'sortBy', 'sortOrder'
      ];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (viewType && viewType !== 'S') params.set('viewType', viewType);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortBy && sortBy !== 'date') params.set('sortBy', sortBy);
      if (sortOrder && sortOrder !== 'desc') params.set('sortOrder', sortOrder);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    statusFilter,
    viewType,
    dateRange,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    setSearchParams
  ]);

  // Selection states
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [paymentCustomerId, setPaymentCustomerId] = useState(null);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState(null);
  const [paymentCustomerName, setPaymentCustomerName] = useState('');
  const [showBulkPaymentImport, setShowBulkPaymentImport] = useState(false);

  // Back button handler
  const handleBack = () => {
    navigate(-1);
  };

  // Email validation function
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return { isValid: false, error: 'Email is required' };
    }
    // Sanitize email (allows @ . - _)
    const sanitized = sanitizeSearchInput(email);
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitized)) {
      return { isValid: false, error: 'Please enter a valid email address' };
    }
    if (sanitized.length > FIELD_LIMITS.search) {
      return { isValid: false, error: `Email must be ${FIELD_LIMITS.search} characters or less` };
    }
    return { isValid: true, error: '' };
  };

  // Handler for clearing all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('all');
    setViewType('S');
    setDateRange({ startDate: '', endDate: '' });
    setCurrentPage(1);
    setSelectedRows(new Set());
  };

  // Normalise a delivery status (code or label) to a canonical key
  const normaliseStatus = (status) => {
    if (!status) return '';
    const s = status.trim().toUpperCase();
    // Short codes returned by some paths
    if (['DEL', 'INT', 'OOD', 'CAN', 'RTO', 'RTD'].includes(s)) return s;
    // Full label strings returned by Shipway API
    if (s === 'DELIVERED') return 'DEL';
    if (s === 'IN TRANSIT') return 'INT';
    if (s === 'OUT FOR DELIVERY') return 'OOD';
    if (s === 'CANCELLED' || s === 'CANCEL') return 'CAN';
    if (s === 'RTO INITIATED' || s === 'RETURN TO ORIGIN') return 'RTO';
    if (s === 'RTO DELIVERED') return 'RTD';
    return s;
  };

  // Helper for delivery status colors
  const getStatusColor = (status) => {
    const s = normaliseStatus(status);
    switch (s) {
      case 'DEL': return 'bg-green-100 text-green-700 border border-green-200';
      case 'INT': return 'bg-blue-100 text-blue-700 border border-blue-200';
      case 'OOD': return 'bg-orange-100 text-orange-700 border border-orange-200';
      case 'CAN': return 'bg-red-100 text-red-700 border border-red-200';
      case 'RTO': return 'bg-red-100 text-red-700 border border-red-200';
      case 'RTD': return 'bg-purple-100 text-purple-700 border border-purple-200';
      default: return 'bg-gray-100 text-gray-600 border border-gray-200';
    }
  };

  // Helper for delivery status icons
  const getStatusIcon = (status) => {
    const s = normaliseStatus(status);
    switch (s) {
      case 'DEL': return <CheckCircle className="h-3 w-3" />;
      case 'INT': return <Truck className="h-3 w-3" />;
      case 'OOD': return <Clock className="h-3 w-3" />;
      case 'CAN': return <X className="h-3 w-3" />;
      case 'RTO':
      case 'RTD': return <AlertCircle className="h-3 w-3" />;
      default: return null;
    }
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = invoices.map(inv => inv._id || inv.id).filter(Boolean);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    if (!id) return;
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Sorting handler
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // View invoice detail
  const handleViewInvoice = (invoice) => {
    setSelectedOrder(invoice);
    setShowOrderModal(true);
  };

  const handleOpenCustomer360 = (customerReferenceId) => {
    if (!customerReferenceId) return;
    navigate(`/customer_360/${encodeURIComponent(customerReferenceId)}`);
  };

  const openAddPaymentModal = (invoice) => {
    if (!invoice?.customerMongoId) {
      toast.error('Customer mapping is required to record a payment');
      return;
    }
    setPaymentCustomerId(invoice.customerMongoId);
    setPaymentInvoiceId(invoice.id || invoice._id);
    setPaymentCustomerName(invoice.partyName || '');
    setShowAddPaymentModal(true);
  };

  const closeAddPaymentModal = () => {
    setShowAddPaymentModal(false);
    setPaymentCustomerId(null);
    setPaymentInvoiceId(null);
    setPaymentCustomerName('');
  };

  const handlePaymentSaved = () => {
    fetchInvoices();
  };

  // Tracking link handler
  const handleTrackClick = (item) => {
    if (item.trackingUrl) {
      window.open(item.trackingUrl, '_blank', 'noopener,noreferrer');
    } else if (item.trackingNumber) {
      toast.info(`Tracking Number: ${item.trackingNumber}`);
    }
  };

  // Sync delivery status from Shipway
  const handleSyncStatus = async () => {
    setSyncing(true);
    try {
      const result = await orderService.syncShipments();
      if (result.success) {
        if (result.isBackground) {
          setIsSyncingInBackground(true);
          toast.success(result.message || 'Sync started in background');
        } else {
          const successCount = result.results?.success || 0;
          const totalCount = result.results?.total || 0;
          toast.success(
            successCount > 0
              ? `Successfully synced ${successCount} shipments out of ${totalCount}`
              : result.message || 'Delivery status sync completed'
          );
          // Refresh after short delay
          setTimeout(fetchInvoices, 2000);
        }
      } else {
        toast.error(result.error || 'Failed to sync delivery status');
      }
    } catch (error) {
      toast.error('Failed to sync delivery status');
    } finally {
      if (!isSyncingInBackground) {
        setSyncing(false);
      }
    }
  };

  // Sync status polling effect
  useEffect(() => {
    let pollInterval = null;

    if (isSyncingInBackground) {
      setSyncing(true);
      pollInterval = setInterval(async () => {
        try {
          const status = await orderService.getSyncStatus();
          if (status.success && !status.isRunning) {
            setIsSyncingInBackground(false);
            setSyncing(false);
            toast.success('Background delivery status sync completed');
            fetchInvoices();
            clearInterval(pollInterval);
          }
        } catch (error) {
        }
      }, 10000); // Poll every 10 seconds
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [isSyncingInBackground]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch global KPIs when viewType or dateRange changes
  useEffect(() => {
    const fetchGlobalKpis = async () => {
      try {
        const result = await get('/crm/invoices/kpis', {
          params: { 
            invoiceType: viewType,
            startDate: dateRange.startDate || undefined,
            endDate: dateRange.endDate || undefined,
            search: debouncedSearchTerm || undefined,
            shipmentStatus: statusFilter !== 'all' ? statusFilter : undefined
          }
        });
        if (result.success) {
          setGlobalKpis({
            totalInvoices: result.kpis.totalInvoices || 0,
            totalRevenue: result.kpis.totalRevenue || 0,
            totalPaid: result.kpis.totalPaid || 0,
            totalOutstanding: result.kpis.totalOutstanding || 0,
            delivered: result.kpis.delivered || 0,
            inTransit: result.kpis.inTransit || 0,
            outForDelivery: result.kpis.outForDelivery || 0,
            rto: result.kpis.rto || 0,
          });
        }
      } catch (error) {
      }
    };
    fetchGlobalKpis();
  }, [viewType, dateRange.startDate, dateRange.endDate, debouncedSearchTerm, statusFilter]);

  // Items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  // Fetch invoices core function
  const fetchInvoices = async () => {
    setLoading(true);
    try {

      const result = await get('/crm/invoices', {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearchTerm || undefined,
          shipmentStatus: statusFilter !== 'all' ? statusFilter : undefined,
          startDate: dateRange.startDate || undefined,
          endDate: dateRange.endDate || undefined,
          sortBy: sortBy,
          sortOrder: sortOrder,
          invoiceType: viewType,
          view: 'list'
        }
      });

      if (result.success && result.data) {
        const transformedInvoices = result.data.map(invoice => ({
          invoiceTypeCode: invoice.rawData?.Type || invoice.type || viewType || 'N/A',
          customerContactName: invoice.customerId?.contactPerson || '',
          customerCompanyName: invoice.customerId?.companyName || '',
          customerAddress1: invoice.customerId?.billingAddress?.street || invoice.customerId?.shippingAddress?.street || '',
          customerAddress2: invoice.customerId?.billingAddress?.street2 || invoice.customerId?.shippingAddress?.street2 || '',
          customerAddress3: invoice.customerId?.billingAddress?.street3 || invoice.customerId?.shippingAddress?.street3 || '',
          customerPinCode: invoice.customerId?.billingAddress?.pincode || invoice.customerId?.shippingAddress?.pincode || '',
          customerPhones: Array.isArray(invoice.customerId?.phone)
            ? invoice.customerId.phone.filter(Boolean).join(' | ')
            : (invoice.customerId?.phone || ''),
          id: invoice._id,
          customerMongoId: invoice.customerId?._id || null,
          customerReferenceId: invoice.customerId?.customerId || '',
          invoiceId: invoice.invoiceNumber || invoice._id,
          orderId: invoice.invoiceNumber || 'N/A',
          partyName: invoice.customerId?.companyName || invoice.customerId?.contactPerson || 'Unknown Customer',
          partyCode: invoice.margCustomerId || '',
          date: invoice.invoiceDate,
          invoiceDate: invoice.invoiceDate,
          totalAmount: invoice.totalAmount || 0,
          paidAmount: invoice.paidAmount || 0,
          // Derived from totalAmount - paidAmount; the outstandingAmount cache is unreliable for Marg-synced invoices.
          outstandingAmount: Math.max(0, (invoice.totalAmount || 0) - (invoice.paidAmount || 0)),
          paymentStatus: invoice.paymentStatus || 'PENDING',
          isFullyPaid: Boolean(invoice.isFullyPaid),
          itemCount: invoice.itemCount ?? (invoice.lineItems ? invoice.lineItems.length : 0),
          items: invoice.lineItems || [],
          trackingUrl: invoice.trackingUrl || '',
          trackingNumber: invoice.trackingNumber || '',
          deliveryStatus: invoice.deliveryStatus || '',
          deliveryStatusLabel: invoice.deliveryStatusLabel || invoice.deliveryStatus || '',
          shipwayTracking: invoice.shipwayTracking || null
        }));

        setInvoices(transformedInvoices);

        if (result.summary) {
          setPaymentSummary({
            totalPaid: result.summary.totalPaid || 0,
            totalOutstanding: result.summary.totalOutstanding || 0
          });
        }

        if (result.pagination) {
          const paginationData = result.pagination;
          setPagination({
            currentPage: paginationData.currentPage || paginationData.page || 1,
            totalPages: paginationData.totalPages || paginationData.pages || 1,
            totalOrders: paginationData.totalRecords || paginationData.totalOrders || paginationData.total || transformedInvoices.length || 0,
            hasNextPage: paginationData.hasNextPage ?? ((paginationData.currentPage || 1) < (paginationData.totalPages || 1)),
            hasPrevPage: paginationData.hasPrevPage ?? ((paginationData.currentPage || 1) > 1),
            limit: paginationData.limit || itemsPerPage
          });
        }

        if (currentPage === 1 && !debouncedSearchTerm && statusFilter === 'all' && !dateRange.startDate && !dateRange.endDate) {
          setAnalytics({
            totalInvoices: result.pagination.totalRecords || result.pagination.totalOrders || transformedInvoices.length || 0,
            totalRevenue: result.pagination.totalValue || 0
          });
        }
      } else {
        toast.error(result.error || 'Failed to fetch invoices');
      }
    } catch (error) {
      toast.error('Failed to fetch invoices. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Main fetch effect
  useEffect(() => {
    fetchInvoices();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, statusFilter, dateRange, sortBy, sortOrder, viewType]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      debouncedSearchTerm.trim() !== '' ||
      sortBy !== 'date' ||
      sortOrder !== 'desc' ||
      statusFilter !== 'all' ||
      viewType !== 'S' ||
      dateRange.startDate !== '' ||
      dateRange.endDate !== ''
    );
  }, [debouncedSearchTerm, sortBy, sortOrder, statusFilter, dateRange]);

  // Reset selection on filter/search/page change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [debouncedSearchTerm, dateRange, itemsPerPage, currentPage]);

  const getSortedInvoices = (data) => {
    if (!sortBy || !data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;
      if (sortBy === 'invoiceId') {
        aValue = (a.invoiceId || '').toLowerCase();
        bValue = (b.invoiceId || '').toLowerCase();
      } else if (sortBy === 'partyName') {
        aValue = (a.partyName || '').toLowerCase();
        bValue = (b.partyName || '').toLowerCase();
      } else if (sortBy === 'totalAmount') {
        aValue = Number(a.totalAmount) || 0;
        bValue = Number(b.totalAmount) || 0;
      } else if (sortBy === 'date') {
        aValue = new Date(a.invoiceDate || a.date || 0).getTime();
        bValue = new Date(b.invoiceDate || b.date || 0).getTime();
      } else {
        aValue = a[sortBy] || '';
        bValue = b[sortBy] || '';
      }

      const order = sortOrder === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  const mapInvoicesForExport = (data) => {
    const invoiceTypeLabelMap = {
      S: 'Sales (S)',
      P: 'Purchase (P)',
      V: 'Order (O)',
      O: 'Order (O)'
    };

    return data.map(inv => ({
      'Invoice ID': inv.invoiceId || 'N/A',
      'Invoice Type': invoiceTypeLabelMap[inv.invoiceTypeCode] || inv.invoiceTypeCode || 'N/A',
      'Customer': inv.partyName || 'N/A',
      'Customer ID': inv.customerReferenceId || 'N/A',
      'Items': inv.itemCount || 0,
      'Total Amount': `₹${formatIndianAmount(inv.totalAmount || 0)}`,
      'Paid Amount': `₹${formatIndianAmount(inv.paidAmount || 0)}`,
      'Outstanding Amount': `₹${formatIndianAmount(inv.outstandingAmount || 0)}`,
      'Payment Status': inv.paymentStatus || 'PENDING',
      'Date': inv.invoiceDate ? formatDate(inv.invoiceDate, { includeTime: false }) : 'N/A',
      'Delivery Status': inv.deliveryStatusLabel || inv.deliveryStatus || 'N/A',
      'Customer Contact Name': inv.customerContactName || 'N/A',
      'Company Name': inv.customerCompanyName || 'N/A',
      'Address1': inv.customerAddress1 || 'N/A',
      'Address2': inv.customerAddress2 || 'N/A',
      'Address3': inv.customerAddress3 || 'N/A',
      'Pin Code': inv.customerPinCode || 'N/A',
      'Phone (All)': inv.customerPhones || 'N/A'
    }));
  };

  /**
   * Unified Backend Export Handler
   */
  const requestBackendExport = useCallback(
    async (scope, format, meta = {}) => {
      if (exportLoading) return;

      // For the "Export Date Range" scope, use the dates chosen in the export
      // modal (passed via meta), not the page's current date filter.
      const isDateRangeScope = scope === 'dateRange';
      const effectiveStartDate = isDateRangeScope ? meta.startDate : dateRange.startDate;
      const effectiveEndDate = isDateRangeScope ? meta.endDate : dateRange.endDate;

      setExportLoading(true);
      const tid = toast.loading('Preparing export...', { id: 'invoice-export' });

      try {
        const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            },
          body: JSON.stringify({
            module: 'invoice-management',
            format,
            scope,
            selectedIds: scope === 'selected' ? Array.from(selectedRows) : [],
            currentPageIds: scope === 'currentPage' ? invoices.map(inv => inv._id || inv.id).filter(Boolean) : [],
            page: currentPage,
            limit: itemsPerPage,
            filters: {
              search: debouncedSearchTerm,
              startDate: effectiveStartDate,
              endDate: effectiveEndDate,
              shipmentStatus: statusFilter,
              invoiceType: viewType
            },
            sortBy,
            sortOrder,
          }),
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Export request failed');
        }

        const blob = await response.blob();
        const disposition = response.headers.get('content-disposition') || '';
        const filenameMatch =
          disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
          disposition.match(/filename="?([^";]+)"?/i);
        
        const filename = filenameMatch?.[1]
          ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
          : generateExportFilename('invoices', format);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Export downloaded successfully', { id: tid });
        setShowExportModal(false);
      } catch (error) {
        toast.error(error.message || 'Export failed', { id: tid });
      } finally {
        setExportLoading(false);
      }
    },
    [debouncedSearchTerm, dateRange, statusFilter, viewType, sortBy, sortOrder, selectedRows, exportLoading]
  );

  const handleOrderScopedExport = (scope, format, meta = {}) => {
    requestBackendExport(scope, format, meta);
  };

  const handleOrderSelectedFormatExport = (format) => {
    requestBackendExport('selected', format);
    setShowInlineSelectedFormats(false);
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((prev) => !prev);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Management</h1>
              <p className="text-gray-600">Manage invoices, track status, and view delivery details</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <PermissionGate permission="customer_payment" action="create">
              <Button
                variant="outline"
                icon={<Upload className="h-4 w-4" />}
                onClick={() => setShowBulkPaymentImport(true)}
              >
                Bulk Payment Import
              </Button>
            </PermissionGate>
            <PermissionGate permission="order_management" action="read">
              <div className="flex items-center space-x-2 relative" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  icon={<Download className="h-4 w-4" />}
                  onClick={handleExportClick}
                  disabled={!invoices || invoices.length === 0}
                >
                  {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
                </Button>

                <SelectedExportFormatBar
                  show={showInlineSelectedFormats}
                  isOpen={showInlineSelectedFormats}
                  panelEntered={true}
                  onExport={handleOrderSelectedFormatExport}
                  loading={exportLoading}
                  className="!right-0 !left-auto !translate-x-0 !z-[9999]"
                />
              </div>
              <Button
                variant="outline"
                icon={<Truck className={`h-4 w-4 ${syncing ? 'animate-bounce' : ''}`} />}
                onClick={handleSyncStatus}
                loading={syncing}
                disabled={syncing}
              >
                {syncing ? 'Syncing...' : 'Sync Delivery Status'}
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Analytics Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Package className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">
                {viewType === 'S' ? 'Total Sales' : viewType === 'P' ? 'Total Purchases' : 'Total Orders'}
              </p>
              <p className="text-xl font-bold text-gray-900">
                {formatIndianNumber(globalKpis.totalInvoices || analytics.totalInvoices || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <span className="text-green-600 font-bold">₹</span>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-xl font-bold text-gray-900">
                ₹{formatIndianAmount(globalKpis.totalRevenue || analytics.totalRevenue || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <IndianRupee className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Paid</p>
              <p className="text-xl font-bold text-gray-900">
                ₹{formatIndianAmount(paymentSummary.totalPaid || globalKpis.totalPaid || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 rounded-lg">
              <IndianRupee className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Outstanding</p>
              <p className="text-xl font-bold text-gray-900">
                ₹{formatIndianAmount(paymentSummary.totalOutstanding || globalKpis.totalOutstanding || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Delivered</p>
              <p className="text-xl font-bold text-gray-900">{formatIndianNumber(globalKpis.delivered)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Truck className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">In Transit</p>
              <p className="text-xl font-bold text-gray-900">{formatIndianNumber(globalKpis.inTransit)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 rounded-lg">
              <Clock className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Out for Delivery</p>
              <p className="text-xl font-bold text-gray-900">{formatIndianNumber(globalKpis.outForDelivery)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">RTO</p>
              <p className="text-xl font-bold text-gray-900">{formatIndianNumber(globalKpis.rto || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                id="order-search-input"
                name="search"
                ref={searchInputRef}
                type="text"
                placeholder="Search invoice id, customers..."
                value={searchTerm}
                onChange={(e) => {
                  handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search);
                  setCurrentPage(1);
                }}
                maxLength={FIELD_LIMITS.search}
                className="pl-10 pr-4 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0 lg:items-center">
            <select
              id="order-viewtype-select"
              name="viewType"
              value={viewType}
              onChange={(e) => {
                setViewType(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full sm:w-32 px-3 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm font-medium"
            >
              <option value="S">Sales</option>
              <option value="P">Purchase</option>
              <option value="V">Order</option>
            </select>
            <select
              id="order-status-select"
              name="status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full max-w-sm px-3 py-2 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm"
            >
              <option value="all">All Status</option>
              <option value="INT">In Transit</option>
              <option value="OOD">Out for Delivery</option>
              <option value="DEL">Delivered</option>
              <option value="RTO">RTO</option>
            </select>
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(newRange) => {
                setDateRange(newRange);
                setCurrentPage(1);
              }}
              allowFutureDates={true}
            />
            {hasActiveFilters && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={handleClearFilters}
                className="flex-shrink-0 whitespace-nowrap h-10"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading invoices...</p>
            </div>
          </div>
        )}

        {/* Invoices Table View */}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left w-12">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green cursor-pointer"
                      onChange={handleSelectAll}
                      checked={invoices.length > 0 && invoices.every(inv => selectedRows.has(inv._id || inv.id))}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('invoiceId')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Invoice Id</span>
                      {sortBy === 'invoiceId' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('partyName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Customer</span>
                      {sortBy === 'partyName' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Items
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('totalAmount')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Amount</span>
                      {sortBy === 'totalAmount' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Outstanding
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery status
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Invoice Date</span>
                      {sortBy === 'date' ? (
                        sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium">No invoices found</p>
                        <p className="text-sm mt-2">
                          {pagination?.totalOrders === 0
                            ? 'No invoices have been synced yet.'
                            : 'Try adjusting your filters to see more results.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  getSortedInvoices(invoices).map((invoice) => {
                    const itemId = invoice._id || invoice.id;
                    const isSelected = selectedRows.has(itemId);
                    const isSalesInvoice = invoice.invoiceTypeCode === 'S';
                    const canRecordPayment = isSalesInvoice && !invoice.isFullyPaid && invoice.customerMongoId;
                    return (
                      <tr key={itemId} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green cursor-pointer"
                            checked={isSelected}
                            onChange={() => handleSelectRow(itemId)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="font-medium uppercase">{invoice.invoiceId || 'N/A'}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[220px] capitalize">
                            {invoice.partyName}
                          </div>
                          {invoice.customerReferenceId ? (
                            <button
                              type="button"
                              onClick={() => handleOpenCustomer360(invoice.customerReferenceId)}
                              className="mt-0.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[220px] block"
                              title="Open Customer 360"
                            >
                              {invoice.customerReferenceId}
                            </button>
                          ) : (
                            <p className="mt-0.5 text-xs text-gray-400">No customer ID</p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.itemCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                          ₹{formatIndianAmount(invoice.totalAmount || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{formatIndianAmount(invoice.paidAmount || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-700">
                          ₹{formatIndianAmount(invoice.outstandingAmount || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {invoice.deliveryStatus ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(invoice.deliveryStatus)}`}>
                              {getStatusIcon(invoice.deliveryStatus)}
                              {invoice.deliveryStatusLabel || invoice.deliveryStatus}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">Not Dispatched</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.invoiceDate && formatDate(invoice.invoiceDate, { includeTime: false })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-wrap gap-2">
                            <PermissionGate permission="order_management" action="read">
                              <button
                                onClick={() => handleViewInvoice(invoice)}
                                className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                              >
                                <Eye className="h-3 w-3" />
                                View
                              </button>
                            </PermissionGate>
                            {isSalesInvoice && (
                              <PermissionGate permission="customer_payment" action="create">
                                <button
                                  type="button"
                                  onClick={() => canRecordPayment && openAddPaymentModal(invoice)}
                                  disabled={!canRecordPayment}
                                  className={`flex items-center gap-1 px-3 py-1 text-xs rounded-lg ${
                                    canRecordPayment
                                      ? 'bg-agrosaf-green text-white hover:bg-agrosaf-green-hover'
                                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  }`}
                                  title={
                                    !invoice.customerMongoId
                                      ? 'Customer mapping required'
                                      : invoice.isFullyPaid
                                        ? 'Invoice is fully paid'
                                        : 'Record payment'
                                  }
                                >
                                  <IndianRupee className="h-3 w-3" />
                                  Payment
                                </button>
                              </PermissionGate>
                            )}
                            <button
                              onClick={() => (invoice.trackingUrl || invoice.trackingNumber) && handleTrackClick(invoice)}
                              disabled={!invoice.trackingUrl && !invoice.trackingNumber}
                              className={`flex items-center gap-1 px-3 py-1 text-xs rounded-lg ${(invoice.trackingUrl || invoice.trackingNumber) ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                            >
                              <Truck className="h-3 w-3" />
                              Track
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {
        (() => {
          const totalItems = Number(pagination?.totalOrders || invoices.length || 0);
          const totalPages = Number(pagination?.totalPages || 1);
          const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
          const shouldShowPagination = !loading && totalPages > 1;
          const handlePageChange = (page) => {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          };

          return shouldShowPagination ? (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination?.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((pagination?.currentPage || 1) * effectivePaginationLimit, totalItems)} of {totalItems} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Items per page:</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange((pagination?.currentPage || 1) - 1)}
                    disabled={pagination?.hasPrevPage === false || (pagination?.currentPage || 1) <= 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex items-center space-x-1">
                    {(() => {
                      const tPages = pagination?.totalPages || 1;
                      const cPage = pagination?.currentPage || 1;
                      const pages = [];

                      if (tPages <= 10) {
                        for (let i = 1; i <= tPages; i++) pages.push(i);
                      } else {
                        // pagination logic simplified for brevity
                        pages.push(1);
                        if (cPage > 4) pages.push('ellipsis');
                        const start = Math.max(2, cPage - 2);
                        const end = Math.min(tPages - 1, cPage + 2);
                        for (let i = start; i <= end; i++) pages.push(i);
                        if (cPage < tPages - 3) pages.push('ellipsis');
                        pages.push(tPages);
                      }

                      return pages.map((page, index) => {
                        if (page === 'ellipsis') return <span key={`ellipsis-${index}`} className="px-2 text-gray-500">...</span>;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === cPage
                              ? 'bg-agrosaf-green text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      });
                    })()}
                  </div>
                  <button
                    onClick={() => handlePageChange((pagination?.currentPage || 1) + 1)}
                    disabled={pagination?.hasNextPage === false || (pagination?.currentPage || 1) >= (pagination?.totalPages || 1)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : null;
        })()
      }

      {/* Invoice Detail Modal */}
      {
        showOrderModal && selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Invoice Details - #{selectedOrder.invoiceId || selectedOrder.id}</h2>
                </div>
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Order Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Invoice Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    {selectedOrder.billNumber && (
                      <p><span className="font-medium">Bill Number:</span> {selectedOrder.billNumber}</p>
                    )}
                    {selectedOrder.voucher && (
                      <p><span className="font-medium">Voucher:</span> {selectedOrder.voucher}</p>
                    )}
                    {(selectedOrder.date || selectedOrder.orderDate) && (
                      <p><span className="font-medium">Invoice Date:</span> {formatDate(selectedOrder.orderDate || selectedOrder.date, { includeTime: false })}</p>
                    )}
                    {selectedOrder.financialYear && (
                      <p><span className="font-medium">Financial Year:</span> {selectedOrder.financialYear}</p>
                    )}
                  </div>
                </div>

                {/* Party & Location Information */}
                {(selectedOrder.partyName ||
                  selectedOrder.routeName ||
                  selectedOrder.areaName ||
                  selectedOrder.partyAddress) && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Party & Location
                      </h3>
                      <div className="space-y-2 text-sm">
                        {/* Party - show only human-friendly name */}
                        {selectedOrder.partyName && (
                          <div>
                            <p>
                              <span className="font-medium">Party:</span>{' '}
                              <span className="text-gray-900 font-semibold">
                                {selectedOrder.partyName}
                              </span>
                            </p>
                          </div>
                        )}

                        {/* Route - show friendly name only */}
                        {selectedOrder.routeName && (
                          <div>
                            <p>
                              <span className="font-medium">Route:</span>{' '}
                              <span className="text-gray-900">
                                {selectedOrder.routeName}
                              </span>
                            </p>
                          </div>
                        )}

                        {/* Area - show friendly name only */}
                        {selectedOrder.areaName && (
                          <div>
                            <p>
                              <span className="font-medium">Area:</span>{' '}
                              <span className="text-gray-900">
                                {selectedOrder.areaName}
                              </span>
                            </p>
                          </div>
                        )}

                        {/* Address - already human-friendly */}
                        {selectedOrder.partyAddress && (
                          <div className="mt-3 pt-3 border-t border-gray-300">
                            <p className="font-medium">Address:</p>
                            <div className="mt-1 text-gray-600">
                              {selectedOrder.partyAddress}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>


              {/* Items */}
              <div className="mt-6">
                <h3 className="font-semibold text-gray-900 mb-3">Invoice Items</h3>
                {selectedOrder.items && selectedOrder.items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Free</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedOrder.items.map((item, index) => (
                          <tr key={item.id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {item.productName || item.pName || item.name || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {item.batch || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.quantity || item.qty || 0}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.free ?? 0}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">₹{(item.rate ?? 0).toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">₹{(item.discount ?? 0).toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {item.gst ?? 0}%
                              {item.gstAmount > 0 && (
                                <div className="text-xs text-gray-600">₹{item.gstAmount.toLocaleString()}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {(() => {
                                // Amount from Marg ERP is taxable value (rate × qty - discount)
                                // If amount is 0/missing but we have rate/qty, calculate it
                                let taxableAmount = item.amount ?? 0;
                                if (taxableAmount === 0 && item.rate && (item.quantity || item.qty)) {
                                  const rate = item.rate ?? 0;
                                  const qty = item.quantity || item.qty || 0;
                                  const discount = item.discount ?? 0;
                                  taxableAmount = (rate * qty) - discount;
                                }
                                // Final amount should include GST: taxableAmount + gstAmount
                                const gstAmount = item.gstAmount ?? 0;
                                const finalAmount = taxableAmount + gstAmount;
                                return `₹${finalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="7" className="px-4 py-3 text-right text-sm font-medium text-gray-700">Total:</td>
                          <td className="px-4 py-3 text-sm font-bold text-gray-900">
                            {(() => {
                              // Calculate total exactly as sum of items to ensure it matches visual sum
                              const calculatedTotal = selectedOrder.items?.reduce((sum, item) => {
                                let taxableAmount = item.amount ?? 0;
                                if (taxableAmount === 0 && item.rate && (item.quantity || item.qty)) {
                                  const rate = item.rate ?? 0;
                                  const qty = item.quantity || item.qty || 0;
                                  const discount = item.discount ?? 0;
                                  taxableAmount = (rate * qty) - discount;
                                }
                                const gstAmount = item.gstAmount ?? 0;
                                return sum + taxableAmount + gstAmount;
                              }, 0) || 0;

                              // Use calculated total if items exist, otherwise fallback to stored total
                              const displayTotal = selectedOrder.items?.length > 0 ? calculatedTotal : (selectedOrder.finalAmount || selectedOrder.totalAmount || 0);

                              return `₹${displayTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            })()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No items found for this invoice
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleOrderScopedExport}
        loading={exportLoading}
        module="invoice-management"
        title="Export Invoices"
        scopedFormatExport={{
          onExport: handleOrderScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
      <AddPaymentModal
        isOpen={showAddPaymentModal}
        customerId={paymentCustomerId}
        initialInvoiceId={paymentInvoiceId}
        customerName={paymentCustomerName}
        onClose={closeAddPaymentModal}
        onSuccess={handlePaymentSaved}
      />
      <BulkPaymentImportModal
        isOpen={showBulkPaymentImport}
        onClose={() => setShowBulkPaymentImport(false)}
        onSuccess={handlePaymentSaved}
      />
    </div>
  );
};

export default OrderManagement;

